import { Check, Star, Zap } from 'lucide-react'
import type { StepProps, Currency, BillingCycle, PricingDatum, TokenCharges, PlanInfo } from '@/types/apply'

interface StepPlanProps extends StepProps {
  pricing: Record<string, Record<BillingCycle, Record<Currency, PricingDatum>>>
  defaultCurrency: Currency
  supportedCurrencies: Currency[]
  tokenCharges: TokenCharges
  plans: Record<string, PlanInfo[]>
}

function formatPrice(price: number, currency: Currency) {
  return new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : 'en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: currency === 'INR' ? 0 : 2
  }).format(price)
}

export default function StepPlan({ formData, updateField, errors, clearError, pricing, supportedCurrencies, tokenCharges, plans }: StepPlanProps) {
  const sub = formData.subscription
  const role = formData.role_info.role || 'artist'
  const availablePlans = plans[role] || plans.artist
  const selectedPlan = availablePlans.find(p => p.key === sub.plan)
  // NO FALLBACK. This is the figure the applicant consents to immediately
  // before paying, and a stale literal here understates a real charge — it
  // said 0.10 after the fee moved to 0.12. The server sends token_charges
  // straight from config/payment.yml for every supported currency, so an
  // absent amount means something is wrong; say nothing rather than a number.
  const tokenCharge: number | undefined = tokenCharges?.[sub.currency as keyof TokenCharges]
  const isYearly = sub.billing_cycle === 'yearly'

  function selectPlan(planKey: string) {
    updateField('subscription', 'plan', planKey)
    clearError('plan')
  }

  function getSavingsPercent(planKey: string): number | null {
    if (!isYearly) return null
    const monthly = pricing[planKey]?.monthly?.[sub.currency]
    const yearly = pricing[planKey]?.yearly?.[sub.currency]
    if (!monthly || !yearly) return null
    const monthlyAnnualized = monthly.discounted_price * 12
    if (monthlyAnnualized <= 0) return null
    const savings = Math.round((1 - yearly.discounted_price / monthlyAnnualized) * 100)
    return savings > 0 ? savings : null
  }

  return (
    <div className="space-y-6">
      {/* Currency + Billing toggles */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="apply-label">Currency</label>
          <div className="marketing-toggle">
            {supportedCurrencies.map(c => (
              <button
                key={c}
                type="button"
                aria-pressed={sub.currency === c}
                className={`marketing-toggle-button ${sub.currency === c ? 'is-active' : ''}`}
                onClick={() => updateField('subscription', 'currency', c)}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="apply-label">Billing Cycle</label>
          <div className="marketing-toggle">
            {(['monthly', 'yearly'] as BillingCycle[]).map(cycle => (
              <button
                key={cycle}
                type="button"
                aria-pressed={sub.billing_cycle === cycle}
                className={`marketing-toggle-button ${sub.billing_cycle === cycle ? 'is-active' : ''}`}
                onClick={() => { updateField('subscription', 'billing_cycle', cycle); clearError('billing_cycle') }}
              >
                {cycle === 'yearly' ? 'Yearly' : 'Monthly'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Plan list */}
      <div>
        <label className="apply-label">Choose Your Plan</label>
        {errors.plan && <p className="apply-error-text">{errors.plan}</p>}

        <div className="space-y-3">
          {availablePlans.map(plan => {
            const price = pricing[plan.key]?.[sub.billing_cycle]?.[sub.currency]
            const isSelected = sub.plan === plan.key
            const periodPrice = price?.discounted_price || 0
            const originalPrice = price?.actual_price
            const hasDiscount = originalPrice != null && originalPrice > periodPrice
            const monthlyEquiv = isYearly ? periodPrice / 12 : null
            const savings = getSavingsPercent(plan.key)

            return (
              <button
                key={plan.key}
                type="button"
                onClick={() => selectPlan(plan.key)}
                className="w-full text-left group relative"
                aria-pressed={isSelected}
              >
                {/* Outer glow for selected state */}
                {isSelected && (
                  <div
                    className="absolute -inset-px rounded-2xl pointer-events-none"
                    style={{
                      background: 'linear-gradient(135deg, rgba(255,106,26,0.35), rgba(255,106,26,0.12), rgba(255,106,26,0.3))',
                      filter: 'blur(0.5px)',
                    }}
                  />
                )}

                <div
                  className="relative rounded-2xl p-5 transition-all duration-200"
                  style={{
                    border: isSelected ? '1px solid rgba(255,106,26,0.45)' : '1px solid rgba(255,255,255,0.08)',
                    background: isSelected
                      ? 'rgba(255,106,26,0.05)'
                      : 'rgba(255,255,255,0.02)',
                    boxShadow: isSelected
                      ? '0 0 20px rgba(255,106,26,0.06), inset 0 1px 0 rgba(255,255,255,0.04)'
                      : 'inset 0 1px 0 rgba(255,255,255,0.03)',
                  }}
                >
                  {/* Badge */}
                  {plan.badge && (
                    <div className="mb-3">
                      <span
                        className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide"
                        style={{
                          background: 'linear-gradient(135deg, rgba(255,106,26,0.2), rgba(255,106,26,0.08))',
                          color: '#ff6a1a',
                          border: '1px solid rgba(255,106,26,0.2)',
                        }}
                      >
                        <Star className="h-3 w-3 fill-current" />
                        {plan.badge}
                      </span>
                    </div>
                  )}

                  <div className="flex items-start justify-between gap-5">
                    {/* Left: radio indicator + plan info */}
                    <div className="flex items-start gap-3.5 min-w-0 flex-1">
                      {/* Radio circle */}
                      <div
                        className="mt-0.5 flex-shrink-0 w-[18px] h-[18px] rounded-full flex items-center justify-center transition-all duration-200"
                        style={{
                          border: isSelected ? '2px solid #ff6a1a' : '2px solid rgba(255,255,255,0.2)',
                          background: isSelected ? 'rgba(255,106,26,0.1)' : 'transparent',
                        }}
                      >
                        {isSelected && (
                          <div className="w-2 h-2 rounded-full" style={{ background: '#ff6a1a' }} />
                        )}
                      </div>

                      {/* Plan name + subtitle */}
                      <div className="min-w-0">
                        <h3 className="text-[15px] font-semibold text-white leading-snug">
                          {plan.name}
                        </h3>
                        <p className="mt-0.5 text-[13px] leading-snug text-white/40">{plan.subtitle}</p>
                        <p className="mt-1 text-[11px] font-medium text-white/30">{plan.royalties} royalties</p>
                      </div>
                    </div>

                    {/* Right: pricing */}
                    {price && (
                      <div className="flex-shrink-0 text-right">
                        {/* Strikethrough original price */}
                        {hasDiscount && (
                          <div className="text-xs text-white/25 line-through mb-0.5">
                            {formatPrice(originalPrice, sub.currency)}
                          </div>
                        )}

                        {/* Main price */}
                        <div className="text-xl font-bold text-white tabular-nums leading-none">
                          {formatPrice(periodPrice, sub.currency)}
                          <span className="text-[11px] font-normal text-white/30 ml-0.5">
                            {isYearly ? '/yr' : '/mo'}
                          </span>
                        </div>

                        {/* Monthly equivalent for yearly */}
                        {monthlyEquiv != null && (
                          <div className="text-[12px] text-white/35 mt-1 tabular-nums">
                            {formatPrice(monthlyEquiv, sub.currency)}/mo
                          </div>
                        )}

                        {/* Savings badge */}
                        {savings != null && (
                          <div className="mt-1.5">
                            <span
                              className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold"
                              style={{ background: 'rgba(34,197,94,0.12)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.15)' }}
                            >
                              <Zap className="h-2.5 w-2.5" />
                              Save {savings}%
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* Selected plan features */}
      {selectedPlan && selectedPlan.bullets.length > 0 && (
        <div
          className="rounded-xl px-5 py-4"
          style={{
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <div className="text-[11px] font-semibold uppercase tracking-widest text-white/30 mb-3">
            What's included in {selectedPlan.name}
          </div>
          <ul className="grid gap-x-5 gap-y-2 sm:grid-cols-2">
            {selectedPlan.bullets.map(bullet => (
              <li key={bullet} className="flex items-start gap-2.5 text-[13px] text-white/60 leading-snug">
                <Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-emerald-500/80" />
                {bullet}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Invitation code */}
      <div>
        <label className="apply-label">
          Invitation Code <span className="text-white/30">(optional)</span>
        </label>
        <input
          type="text"
          className="apply-input"
          placeholder="Enter your invitation code"
          value={sub.invitation_code}
          onChange={e => updateField('subscription', 'invitation_code', e.target.value)}
        />
      </div>

      {/* Verification notice */}
      <div className="rounded-xl border border-[#ff6a1a]/20 bg-[#ff6a1a]/[0.06] px-4 py-3">
        <p className="text-sm text-white/70" data-testid="verification-notice">
          {tokenCharge === undefined ? (
            <>A small verification charge will be applied to confirm your identity.</>
          ) : (
            <>
              A small verification charge of{' '}
              <strong className="text-[#ff6a1a]">{formatPrice(tokenCharge, sub.currency)}</strong>
              {' '}will be applied to confirm your identity.
            </>
          )}
          {' '}This charge is non-refundable.
        </p>
      </div>

      {/* Required agreements — gating the submit button. Each label links to
          the full policy page so the applicant can read what they're agreeing
          to. Submit stays disabled (in Apply.tsx) until all three are true. */}
      <fieldset className="space-y-3 pt-2">
        <legend className="apply-label">Before you submit</legend>

        <AgreementCheckbox
          checked={formData.agreements.terms_of_service}
          onChange={(v) => { updateField('agreements', 'terms_of_service', v); clearError('agreements.terms_of_service') }}
          error={errors['agreements.terms_of_service']}
        >
          I have read and agree to wavbee&apos;s{' '}
          <a href="/user-agreement" target="_blank" rel="noopener" className="text-[#ff6a1a] underline">
            Terms of Service
          </a>{' '}and User Agreement.
        </AgreementCheckbox>

        <AgreementCheckbox
          checked={formData.agreements.ai_music_policy}
          onChange={(v) => { updateField('agreements', 'ai_music_policy', v); clearError('agreements.ai_music_policy') }}
          error={errors['agreements.ai_music_policy']}
        >
          I confirm my music is not entirely AI-generated. Any AI involvement uses fairly-trained models —{' '}
          <a href="/ai-music-policy" target="_blank" rel="noopener" className="text-[#ff6a1a] underline">
            see the AI Music Policy
          </a>.
        </AgreementCheckbox>

        <AgreementCheckbox
          checked={formData.agreements.reapply_policy}
          onChange={(v) => { updateField('agreements', 'reapply_policy', v); clearError('agreements.reapply_policy') }}
          error={errors['agreements.reapply_policy']}
        >
          I have not previously been rejected or banned for copyright, fraud, or platform abuse. I understand
          that if I have been, this application will not be accepted and the verification charge will not be
          refunded —{' '}
          <a href="/reapply-policy" target="_blank" rel="noopener" className="text-[#ff6a1a] underline">
            see the Reapplication Policy
          </a>.
        </AgreementCheckbox>
      </fieldset>
    </div>
  )
}

function AgreementCheckbox({
  checked, onChange, error, children
}: {
  checked: boolean
  onChange: (next: boolean) => void
  error?: string
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-white/20 bg-white/5 text-[#ff6a1a] focus:ring-[#ff6a1a]/40"
        />
        <span className="text-sm leading-relaxed text-white/70">
          {children}
        </span>
      </label>
      {error && (
        <p className="ml-7 mt-1 text-xs text-red-400">{error}</p>
      )}
    </div>
  )
}
